/**
 * NP-442 — self-healing lazy routes after a redeploy.
 *
 * The app is code-split with content-hashed chunk names. `index.html` is served
 * `no-store` while `/assets/*` is `immutable, max-age=1y`, and a page chunk's
 * filename appears ONLY inside the loaded index bundle
 * (see public/staticwebapp.config.json).
 *
 * So a client that loaded the app before a deploy holds an index bundle that
 * points at `Register-<oldhash>.js`. Dev redeploys many times a day; after one,
 * that file no longer exists. The next navigation to a not-yet-loaded route
 * rejects with "Failed to fetch dynamically imported module", React's lazy
 * boundary rethrows it during render, and the user gets a dead error page. The
 * "idle time" in the bug report is just the window in which a deploy landed.
 *
 * The condition is fully recoverable: re-requesting `index.html` yields the new
 * manifest. So on a chunk-load failure we reload once and let the fresh bundle
 * resolve the correct hashed URL.
 *
 * Loop safety — a reload budget of ONE per route per tab session:
 *   - keyed per route, so a later deploy affecting a different route can still
 *     self-heal instead of being starved by an earlier unrelated failure;
 *   - a second failure for the same route rethrows, so a permanently missing
 *     asset shows a real error instead of reloading forever;
 *   - a successful load clears that route's budget, so a client left open
 *     across several deploys keeps working;
 *   - `sessionStorage`, so the budget dies with the tab rather than sticking to
 *     the origin forever.
 */
import { lazy, type ComponentType } from "react";

const STORAGE_PREFIX = "path_chunk_reload:";

/**
 * Browser messages for a failed dynamic import. There is no standard error type
 * or code, so the message is all we get:
 *   Chrome/Edge  "Failed to fetch dynamically imported module: <url>"
 *   Firefox      "error loading dynamically imported module"
 *   Safari       "Importing a module script failed."
 *   webpack-era  "ChunkLoadError" / "Loading chunk N failed"
 */
const CHUNK_ERROR_PATTERN =
  /(failed to (fetch|load) dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|loading chunk \S+ failed|failed to load module script)/i;

export function isChunkLoadError(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const name = error instanceof Error ? error.name : "";
  if (name === "ChunkLoadError") return true;
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return CHUNK_ERROR_PATTERN.test(message);
}

function storageKey(routeKey: string): string {
  return `${STORAGE_PREFIX}${routeKey}`;
}

/** sessionStorage throws in some privacy modes — never let that mask the real error. */
function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function hasSpentReloadBudget(routeKey: string): boolean {
  return safeSession()?.getItem(storageKey(routeKey)) === "1";
}

function spendReloadBudget(routeKey: string): void {
  try {
    safeSession()?.setItem(storageKey(routeKey), "1");
  } catch {
    /* budget is best-effort; a failure here only risks one extra reload */
  }
}

export function clearReloadBudget(routeKey: string): void {
  try {
    safeSession()?.removeItem(storageKey(routeKey));
  } catch {
    /* ignore */
  }
}

/**
 * True only when the browser is *definitely* offline. `navigator.onLine === true`
 * is famously unreliable, but `false` is trustworthy — and it is the case that
 * matters: a genuinely offline user must see a real error, not a reload loop.
 */
function isDefinitelyOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Drop-in replacement for `React.lazy` that recovers from a stale chunk.
 *
 * @param routeKey stable identifier for this route's reload budget. Must not
 *                 change between deploys — use the component name, not the
 *                 hashed filename.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  routeKey: string,
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().then(
      (mod) => {
        // Loaded fine: give this route its budget back so a client kept open
        // across multiple deploys can self-heal again.
        clearReloadBudget(routeKey);
        return mod;
      },
      (error: unknown) => {
        const recoverable =
          isChunkLoadError(error) &&
          !isDefinitelyOffline() &&
          !hasSpentReloadBudget(routeKey);

        if (!recoverable) throw error;

        spendReloadBudget(routeKey);
        console.warn(
          `[lazyWithReload] Stale chunk for "${routeKey}" — reloading once to pick up the new asset manifest.`,
          error,
        );
        window.location.reload();

        // Resolve never: the document is being replaced, and rethrowing here
        // would flash an error page during the reload.
        return new Promise<{ default: T }>(() => {});
      },
    ),
  );
}
